import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import debounce from 'lodash/debounce';

import {
  changeCurrentSearchValue,
  changeDebouncedSearchValue,
  resetSearchValue,
} from 'store/search/actions';
import { getCurrentSearchValue } from 'store/search/selectors';

const useSearch = ({ debounceTimeout = 0, shouldResetValueOnUnmount = true } = {}) => {
  const dispatch = useDispatch();
  const currentSearchValue = useSelector(getCurrentSearchValue);

  const changeDebouncedValue = useCallback(
    newSearchValue => {
      dispatch(changeDebouncedSearchValue(newSearchValue));
    },
    [dispatch],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedChangeDebouncedValue = useCallback(
    debounce(changeDebouncedValue, debounceTimeout),
    [changeDebouncedValue, debounceTimeout],
  );

  const onSearchValueChange = useCallback(
    newSearchValue => {
      dispatch(changeCurrentSearchValue(newSearchValue));
      debouncedChangeDebouncedValue(newSearchValue);
    },
    [dispatch, debouncedChangeDebouncedValue],
  );

  useEffect(() => {
    return () => {
      debouncedChangeDebouncedValue.cancel();

      if (shouldResetValueOnUnmount) {
        dispatch(resetSearchValue());
      }
    };
  }, [dispatch, debouncedChangeDebouncedValue, shouldResetValueOnUnmount]);

  return { searchText: currentSearchValue, setSearchText: onSearchValueChange };
};

export default useSearch;
